import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import emailjs from '@emailjs/browser';
import PageHero from '../components/PageHero';
import styles from '../styles/ContactUsPage.module.css';

const schema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').regex(/^[^\d]+$/, 'First name cannot contain numbers'),
  lastName: z.string().trim().min(1, 'Last name is required').regex(/^[^\d]+$/, 'Last name cannot contain numbers'),
  email: z.string().trim().min(1, 'Email is required').email('Enter a valid email'),
  intent: z.string().min(1, 'Please select an option'),
  subject: z.string().trim().min(1, 'Subject is required'),
  message: z.string().trim().min(1, 'Message is required'),
});

type FormFields = z.infer<typeof schema>;

const Contact = () => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormFields) => {
    setSubmitStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          intent: data.intent,
          subject: data.subject,
          message: data.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );
      setSubmitStatus('success');
      reset();
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <>
      <PageHero
        backgroundImage="/images/Contact/hero-bg.svg"
        title=" Let’s Build Something Resilient"
        subtitle=""
        description="Whether you are an entrepreneur with a vision, a corporate partner looking for impact, or a well-wisher ready to support our communities—your journey with Symbioun starts here."
        breadcrumb={{
          home: "Home",
          current: "Contact Us"
        }}
      />

      <section className={styles.contactPage}>
        <Container>
          <div className={styles.contactInfoContainer}>
            <div className={styles.contactInfoCard}>
              <div className={styles.contactIcon}>
                <img src="/icons/Contact/location.svg" alt="Location" />
              </div>
              <h2>Address</h2>
              <p>Rev Fr Mariyanayagam Centre, <br />RDS Road, Mavadivembu, Batticaloa, Sri Lanka</p>
            </div>

            <div className={styles.contactInfoCard}>
              <div className={styles.contactIcon}>
                <img src="/icons/Contact/email.svg" alt="Email" />
              </div>
              <h2>Email Us</h2>
              <p>info@symbioun.lk</p>
            </div>

            <div className={styles.contactInfoCard}>
              <div className={styles.contactIcon}>
                <img src="/icons/Contact/phone.svg" alt="Phone" />
              </div>
              <h2>Call Now</h2>
              <p>xxx xxxx xxx</p>
            </div>
          </div>

          <div className={styles.contactFormSection}>
            <h2>Find Us Here</h2>
            <p>At Symbioun, we don’t believe in 'one-off' transactions. We believe in life-long partnerships. When you reach out, you aren't just contacting a company; you are connecting with a movement dedicated to uplifting Sri Lankan potential.</p>
          </div>

          <div className={styles.contactFormContainer}>
            <form className={styles.contactPageForm} onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <input
                    {...register('firstName')}
                    type="text"
                    className={`${styles.formInput} ${errors.firstName ? styles.inputError : ''}`}
                    placeholder="First Name"
                  />
                  {errors.firstName && <span className={styles.errorMsg}>{errors.firstName.message}</span>}
                </div>
                <div className={styles.formGroup}>
                  <input
                    {...register('lastName')}
                    type="text"
                    className={`${styles.formInput} ${errors.lastName ? styles.inputError : ''}`}
                    placeholder="Last Name"
                  />
                  {errors.lastName && <span className={styles.errorMsg}>{errors.lastName.message}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <input
                    {...register('email')}
                    type="email"
                    className={`${styles.formInput} ${errors.email ? styles.inputError : ''}`}
                    placeholder="Email"
                  />
                  {errors.email && <span className={styles.errorMsg}>{errors.email.message}</span>}
                </div>
                <div className={styles.formGroup}>
                  <select
                    {...register('intent')}
                    className={`${styles.formInput} ${styles.formSelect} ${errors.intent ? styles.inputError : ''}`}
                    defaultValue=""
                  >
                    <option value="" disabled>Select an option</option>
                    <option value="grow-my-business">Grow My Business</option>
                    <option value="partner-for-impact">Partner for Impact</option>
                    <option value="support-the-community">Support the Community</option>
                    <option value="global-trade-export">Global Trade &amp; Export</option>
                  </select>
                  {errors.intent && <span className={styles.errorMsg}>{errors.intent.message}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <input
                    {...register('subject')}
                    type="text"
                    className={`${styles.formInput} ${errors.subject ? styles.inputError : ''}`}
                    placeholder="Subject"
                  />
                  {errors.subject && <span className={styles.errorMsg}>{errors.subject.message}</span>}
                </div>
              </div>

              <div className={styles.formGroup}>
                <textarea
                  {...register('message')}
                  className={`${styles.formTextarea} ${errors.message ? styles.inputError : ''}`}
                  placeholder="Message"
                ></textarea>
                {errors.message && <span className={styles.errorMsg}>{errors.message.message}</span>}
              </div>

              <button type="submit" className={styles.submitButton} disabled={submitStatus === 'sending'}>
                {submitStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && <p className={styles.successMsg}>Your message has been sent successfully!</p>}
              {submitStatus === 'error' && <p className={styles.errorMsg}>Something went wrong. Please try again.</p>}
            </form>
          </div>
        </Container>
        <div className="contact-map-container">
          <iframe
            title="Our Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3953.731165120861!2d81.6969699!3d7.711969099999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3afacd512e789091%3A0x2d1ebacf20d5a967!2sDiocesan%20Catechetical%20Centre!5e0!3m2!1sen!2slk!4v1748080399087!5m2!1sen!2slk"
            width="100%"
            height="400"
            style={{ border: 0, marginTop: '60px' }}
            allowFullScreen={false}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>
    </>
  );
};

export default Contact;
